import { AgCharts } from 'ag-charts-react'
import { Checkbox } from 'primereact/checkbox'
import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { Dropdown } from 'primereact/dropdown'
import { MultiSelect } from 'primereact/multiselect'
import { FC, useEffect, useMemo, useState } from 'react'
import { useRecoilValue } from 'recoil'
import {
  getSolveMetricDetail,
  getSolveMetricRunTable,
  getSolveMetricStatistics,
  SolveMetricChartDatum,
  SolveMetricKey,
  SolveMetricRunTableRow,
  SolveMetricTableRow
} from '../../api/solve-metrics-API'
import { currentTeamState, teamListState, themeState } from '../../utils/state-atoms'
import {
  CheckboxStyle,
  DropdownStyle,
  MultiSelectCheckboxStyle
} from '../../utils/styling-constants'

const solveMetricStats: {
  key: SolveMetricKey
  label: string
  hematiteValue: string
  raspiteValue: string
  natroliteValue: string
  iolite116CoreValue: string
}[] = [
  {
    key: 'average-solve-time',
    label: 'Average Solve Time',
    hematiteValue: '--',
    raspiteValue: '--',
    natroliteValue: '--',
    iolite116CoreValue: '--'
  },
  {
    key: 'iteration-duration',
    label: 'Iteration Duration',
    hematiteValue: '--',
    raspiteValue: '--',
    natroliteValue: '--',
    iolite116CoreValue: '--'
  },
  {
    key: 'average-iterations-to-solve',
    label: 'Average Iterations to Solve',
    hematiteValue: '--',
    raspiteValue: '--',
    natroliteValue: '--',
    iolite116CoreValue: '--'
  }
]
const solveMetricDateRangeOptions = [
  { label: 'This Week', value: 'this-week' },
  { label: 'This Month', value: 'this-month' },
  { label: 'Past 3 Months', value: 'past-3-months' },
  { label: 'Past Year', value: 'past-year' },
  { label: 'All Time', value: 'all-time' }
]
const currentYear = new Date().getFullYear()
const solveMetricChartYearOptions = [
  { label: 'Year to Date', value: 'ytd' },
  ...Array.from({ length: 4 }, (_, index) => {
    const year = currentYear - index - 1
    return { label: String(year), value: String(year) }
  })
]
const solveMetricTableColumns = {
  'average-solve-time': [
    { field: 'caseId', header: 'Case ID' },
    { field: 'raceTeam', header: 'Race Team' },
    { field: 'hematiteAverageSolveTime', header: 'Hematite Avg Solve Time' },
    { field: 'raspiteAverageSolveTime', header: 'Raspite Avg Solve Time' },
    { field: 'natroliteAverageSolveTime', header: 'Natrolite Avg Solve Time' },
    { field: 'iolite116CoreAverageSolveTime', header: 'Iolite-1 16Core Avg Solve Time' }
  ],
  'iteration-duration': [
    { field: 'caseId', header: 'Case ID' },
    { field: 'raceTeam', header: 'Race Team' },
    { field: 'hematiteIterationDuration', header: 'Hematite Iteration Duration' },
    { field: 'raspiteIterationDuration', header: 'Raspite Iteration Duration' },
    { field: 'natroliteIterationDuration', header: 'Natrolite Iteration Duration' },
    { field: 'iolite116CoreIterationDuration', header: 'Iolite-1 16Core Iteration Duration' }
  ],
  'average-iterations-to-solve': [
    { field: 'caseId', header: 'Case ID' },
    { field: 'raceTeam', header: 'Race Team' },
    { field: 'hematiteAverageIterations', header: 'Hematite Avg Iterations' },
    { field: 'raspiteAverageIterations', header: 'Raspite Avg Iterations' },
    { field: 'natroliteAverageIterations', header: 'Natrolite Avg Iterations' },
    { field: 'iolite116CoreAverageIterations', header: 'Iolite-1 16Core Avg Iterations' }
  ]
} satisfies Record<SolveMetricKey, { field: string; header: string }[]>
const solveMetricRunTableColumns: { field: keyof SolveMetricRunTableRow; header: string }[] = [
  { field: 'run', header: 'Run' },
  { field: 'coreType', header: 'Core Type' },
  { field: 'cellsPerStage', header: '# of Cells Per Stage' },
  { field: 'startTime', header: 'Start Time' },
  { field: 'sumProcessors', header: 'Sum of Processors' },
  { field: 'solveTimeH', header: 'Solve Time (h)' },
  { field: 'wallTimeH', header: 'Wall Time (h)' },
  { field: 'sumCpuHours', header: 'Sum of CPU Hours' },
  { field: 'avgIterationDuration', header: 'Avg Iteration Duration' },
  { field: 'finalIterationAmount', header: 'Final Iteration Amount' },
  { field: 'hpcAsset', header: 'HPC Asset' },
  { field: 'solutionSoftware', header: 'Solution Software' },
  { field: 'user', header: 'User' }
]

const panelClass =
  'rounded border border-gray-200 bg-white p-4 shadow-sm dark:border-neutral-700 dark:bg-neutral-80'
const panelTitleClass = 'mb-3 text-sm font-semibold text-gray-700 dark:text-neutral-10'
const tableStyle = {
  wrapper: { className: 'dark:bg-neutral-80' },
  table: { className: 'dark:bg-neutral-80 dark:text-neutral-10' },
  headerRow: { className: 'dark:bg-neutral-80' },
  headerCell: { className: 'dark:border-neutral-700 dark:bg-neutral-80 dark:text-neutral-10' },
  bodyRow: { className: 'dark:bg-neutral-80 dark:text-neutral-10 dark:hover:bg-neutral-70' },
  bodyCell: { className: 'dark:border-neutral-700 dark:text-neutral-10' },
  emptyMessage: { className: 'dark:bg-neutral-80 dark:text-neutral-60' }
}

type NumberMetricFormatter = (value: number | string | null) => string | number

interface SolveMetricsProps {
  formatWholeNumber: NumberMetricFormatter
  formatTwoDecimalNumber: NumberMetricFormatter
}

const formatStartTime = (value: string | null) => {
  if (!value) return ''

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value

  return date.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short'
  })
}

const SolveMetrics: FC<SolveMetricsProps> = ({ formatWholeNumber, formatTwoDecimalNumber }) => {
  const currentTeam = useRecoilValue(currentTeamState)
  const teamList = useRecoilValue(teamListState)
  const appTheme = useRecoilValue(themeState)
  const [activeSolveMetric, setActiveSolveMetric] = useState<SolveMetricKey>(
    solveMetricStats[0].key
  )
  const [viewSolveMetricTeamControls, setViewSolveMetricTeamControls] = useState(false)
  const [viewAllSolveMetricTeams, setViewAllSolveMetricTeams] = useState(false)
  const [selectedSolveMetricTeams, setSelectedSolveMetricTeams] = useState<string[]>(() =>
    currentTeam ? [currentTeam] : []
  )

  useEffect(() => {
    if (viewAllSolveMetricTeams) return
    setSelectedSolveMetricTeams(currentTeam ? [currentTeam] : [])
  }, [currentTeam, viewAllSolveMetricTeams])

  const [solveMetricDateRanges, setSolveMetricDateRanges] = useState<
    Record<SolveMetricKey, string>
  >(
    () =>
      Object.fromEntries(solveMetricStats.map(stat => [stat.key, 'this-week'])) as Record<
        SolveMetricKey,
        string
      >
  )
  const [solveMetricStatisticValues, setSolveMetricStatisticValues] = useState<
    Record<
      SolveMetricKey,
      {
        hematiteValue: string
        raspiteValue: string
        natroliteValue: string
        iolite116CoreValue: string
      }
    >
  >(
    () =>
      Object.fromEntries(
        solveMetricStats.map(stat => [
          stat.key,
          {
            hematiteValue: stat.hematiteValue,
            raspiteValue: stat.raspiteValue,
            natroliteValue: stat.natroliteValue,
            iolite116CoreValue: stat.iolite116CoreValue
          }
        ])
      ) as Record<
        SolveMetricKey,
        {
          hematiteValue: string
          raspiteValue: string
          natroliteValue: string
          iolite116CoreValue: string
        }
      >
  )
  const [solveMetricChartData, setSolveMetricChartData] = useState<SolveMetricChartDatum[]>([])
  const [solveMetricTableRows, setSolveMetricTableRows] = useState<SolveMetricTableRow[]>([])
  const [solveMetricRunTableRows, setSolveMetricRunTableRows] = useState<SolveMetricRunTableRow[]>(
    []
  )
  const [solveMetricChartYear, setSolveMetricChartYear] = useState('ytd')
  const solveMetricStatCards = solveMetricStats.map(stat => ({
    ...stat,
    ...solveMetricStatisticValues[stat.key]
  }))
  const activeSolveMetricStat = solveMetricStatCards.find(stat => stat.key === activeSolveMetric)
  const activeSolveMetricColumns = solveMetricTableColumns[activeSolveMetric]
  const solveMetricRunTableBodyByField = useMemo<
    Partial<Record<keyof SolveMetricRunTableRow, (row: SolveMetricRunTableRow) => string | number>>
  >(
    () => ({
      cellsPerStage: row => formatWholeNumber(row.cellsPerStage),
      startTime: row => formatStartTime(row.startTime),
      solveTimeH: row => formatTwoDecimalNumber(row.solveTimeH),
      wallTimeH: row => formatTwoDecimalNumber(row.wallTimeH),
      sumCpuHours: row => formatTwoDecimalNumber(row.sumCpuHours),
      avgIterationDuration: row => formatTwoDecimalNumber(row.avgIterationDuration)
    }),
    [formatTwoDecimalNumber, formatWholeNumber]
  )
  const solveMetricTeamOptions = useMemo(() => {
    const teams = teamList.length > 0 ? teamList : currentTeam ? [currentTeam] : []
    return teams.map(team => ({ label: team, value: team }))
  }, [currentTeam, teamList])
  const solveMetricTeamScope = useMemo(
    () => ({
      viewAllTeams: viewAllSolveMetricTeams,
      teams: viewAllSolveMetricTeams ? [] : selectedSolveMetricTeams
    }),
    [selectedSolveMetricTeams, viewAllSolveMetricTeams]
  )

  useEffect(() => {
    if (!currentTeam) return

    setSelectedSolveMetricTeams(prev => {
      if (prev.length > 0 && prev.includes(currentTeam)) {
        return prev
      }

      return [currentTeam]
    })
  }, [currentTeam])

  const activeSolveMetricDateRange = solveMetricDateRanges[activeSolveMetric]
  const solveMetricChartOptions = useMemo(
    () => ({
      data: solveMetricChartData,
      theme: appTheme === 'dark' ? 'ag-default-dark' : 'ag-default',
      background: { fill: 'transparent' },
      title: { text: activeSolveMetricStat?.label || 'Solve Metrics' },
      series: [
        { type: 'bar', xKey: 'month', yKey: 'hematite', yName: 'Hematite', fill: '#16a34a' },
        { type: 'bar', xKey: 'month', yKey: 'raspite', yName: 'Raspite', fill: '#0072ce' },
        { type: 'bar', xKey: 'month', yKey: 'natrolite', yName: 'Natrolite', fill: '#d97706' },
        {
          type: 'bar',
          xKey: 'month',
          yKey: 'iolite116Core',
          yName: 'Iolite-1 16Core',
          fill: '#8b5cf6'
        }
      ],
      axes: [
        { type: 'category', position: 'bottom' },
        { type: 'number', position: 'left' }
      ],
      legend: { position: 'bottom' }
    }),
    [activeSolveMetricStat?.label, appTheme, solveMetricChartData]
  )

  useEffect(() => {
    let isCurrent = true

    void getSolveMetricStatistics({
      teamScope: solveMetricTeamScope,
      dateRanges: solveMetricDateRanges
    })
      .then(response => {
        if (!isCurrent) return

        setSolveMetricStatisticValues(prev => {
          const next = { ...prev }
          response.statistics.forEach(stat => {
            next[stat.key] = {
              hematiteValue: stat.hematiteValue,
              raspiteValue: stat.raspiteValue,
              natroliteValue: stat.natroliteValue,
              iolite116CoreValue: stat.iolite116CoreValue
            }
          })
          return next
        })
      })
      .catch(error => {
        console.error('Failed to load solve metric statistics', error)
      })

    return () => {
      isCurrent = false
    }
  }, [solveMetricDateRanges, solveMetricTeamScope])

  useEffect(() => {
    let isCurrent = true

    void getSolveMetricDetail({
      statKey: activeSolveMetric,
      dateRange: activeSolveMetricDateRange,
      chartYear: solveMetricChartYear,
      teamScope: solveMetricTeamScope
    })
      .then(response => {
        if (!isCurrent) return

        setSolveMetricChartData(response.chartData)
        setSolveMetricTableRows(response.tableRows)
      })
      .catch(error => {
        console.error('Failed to load solve metric detail', error)
      })

    return () => {
      isCurrent = false
    }
  }, [activeSolveMetric, activeSolveMetricDateRange, solveMetricChartYear, solveMetricTeamScope])

  useEffect(() => {
    let isCurrent = true

    void getSolveMetricRunTable({ teamScope: solveMetricTeamScope })
      .then(response => {
        if (!isCurrent) return

        setSolveMetricRunTableRows(response.tableRows)
      })
      .catch(error => {
        console.error('Failed to load solve metric run table', error)
      })

    return () => {
      isCurrent = false
    }
  }, [solveMetricTeamScope])

  return (
    <div className="flex flex-col gap-4 px-4 py-4 text-gray-700 dark:text-neutral-10">
      <div className="flex flex-col gap-2 text-sm text-gray-700 dark:text-neutral-10">
        <div className="flex items-center">
          <Checkbox
            inputId="solve-metrics-view-team-controls"
            checked={viewSolveMetricTeamControls}
            onChange={e => setViewSolveMetricTeamControls(!!e.checked)}
            pt={CheckboxStyle}
          />
          <label
            htmlFor="solve-metrics-view-team-controls"
            className="ml-2 font-normal dark:text-neutral-10">
            View Team Controls
          </label>
        </div>
        {viewSolveMetricTeamControls && (
          <div className="flex flex-col gap-3 pl-6 lg:flex-row lg:items-end">
            <div className="flex items-center pb-1">
              <Checkbox
                inputId="solve-metrics-view-all-teams"
                checked={viewAllSolveMetricTeams}
                onChange={e => setViewAllSolveMetricTeams(!!e.checked)}
                pt={CheckboxStyle}
              />
              <label
                htmlFor="solve-metrics-view-all-teams"
                className="ml-2 font-normal dark:text-neutral-10">
                View all Teams
              </label>
            </div>
            <div className="flex flex-col gap-1 sm:min-w-[320px]">
              <label
                htmlFor="solve-metrics-team-selector"
                className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-neutral-60">
                Teams
              </label>
              <MultiSelect
                inputId="solve-metrics-team-selector"
                value={selectedSolveMetricTeams}
                options={solveMetricTeamOptions}
                onChange={e => setSelectedSolveMetricTeams(e.value)}
                disabled={viewAllSolveMetricTeams}
                display="chip"
                placeholder="Select teams"
                className="min-w-full bg-transparent"
                pt={{
                  ...MultiSelectCheckboxStyle,
                  root: {
                    className:
                      'min-w-full bg-transparent dark:border-neutral-70 dark:bg-transparent'
                  },
                  token: { className: '!py-0' }
                }}
              />
            </div>
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="flex flex-col gap-3 lg:col-span-1">
          {solveMetricStatCards.map(stat => {
            const isActive = activeSolveMetric === stat.key
            return (
              <div
                key={stat.key}
                className={`w-full rounded border bg-white px-4 py-3 text-left shadow-sm transition-colors hover:border-gm-blue hover:text-gm-blue dark:bg-neutral-80 dark:hover:border-light-blue dark:hover:text-light-blue ${
                  isActive
                    ? 'border-gm-blue text-gm-blue dark:border-light-blue dark:text-light-blue'
                    : 'border-gray-200 text-gray-700 dark:border-neutral-700 dark:text-neutral-10'
                }`}
                onClick={() => setActiveSolveMetric(stat.key)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    setActiveSolveMetric(stat.key)
                  }
                }}
                role="button"
                tabIndex={0}>
                <div className="flex items-start justify-between gap-3">
                  <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-neutral-60">
                    {stat.label}
                  </div>
                  <div onClick={e => e.stopPropagation()}>
                    <Dropdown
                      value={solveMetricDateRanges[stat.key]}
                      options={solveMetricDateRangeOptions}
                      onChange={e => {
                        setSolveMetricDateRanges(prev => ({ ...prev, [stat.key]: e.value }))
                      }}
                      className="h-8 min-w-[116px] text-xs"
                      pt={{
                        ...DropdownStyle,
                        root: {
                          className:
                            'h-8 min-w-[116px] bg-transparent dark:border-neutral-70 dark:bg-transparent hover:border-gm-blue dark:hover:border-light-blue'
                        },
                        input: { className: 'py-1 text-xs dark:text-neutral-10' },
                        trigger: { className: 'w-8 dark:text-neutral-10' }
                      }}
                    />
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-4">
                  <div>
                    <div className="text-2xl font-bold leading-none text-green-600">
                      {stat.hematiteValue}
                    </div>
                    <div className="mt-1 text-xs font-semibold text-green-600">Hematite</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold leading-none text-gm-blue">
                      {stat.raspiteValue}
                    </div>
                    <div className="mt-1 text-xs font-semibold text-gm-blue">Raspite</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold leading-none text-amber-600">
                      {stat.natroliteValue}
                    </div>
                    <div className="mt-1 text-xs font-semibold text-amber-600">Natrolite</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold leading-none text-purple-600">
                      {stat.iolite116CoreValue}
                    </div>
                    <div className="mt-1 text-xs font-semibold text-purple-600">
                      Iolite-1 16Core
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
        <div className={`${panelClass} min-h-[320px] lg:col-span-2`}>
          <div className="mb-3 flex items-center justify-between gap-3">
            <div className="text-sm font-semibold text-gray-700 dark:text-neutral-10">
              {activeSolveMetricStat?.label} by Month
            </div>
            <Dropdown
              value={solveMetricChartYear}
              options={solveMetricChartYearOptions}
              onChange={e => setSolveMetricChartYear(e.value)}
              className="h-8 min-w-[132px] text-xs"
              pt={{
                ...DropdownStyle,
                root: {
                  className:
                    'h-8 min-w-[132px] bg-transparent dark:border-neutral-70 dark:bg-transparent hover:border-gm-blue dark:hover:border-light-blue'
                },
                input: { className: 'py-1 text-xs dark:text-neutral-10' },
                trigger: { className: 'w-8 dark:text-neutral-10' }
              }}
            />
          </div>
          <div className="h-[260px] w-full">
            <AgCharts options={solveMetricChartOptions as any} />
          </div>
        </div>
      </div>
      <div className={panelClass}>
        <div className={panelTitleClass}>{activeSolveMetricStat?.label} Statistics</div>
        <DataTable
          value={solveMetricTableRows}
          emptyMessage="No solve metrics data available."
          tableStyle={{ minWidth: '48rem' }}
          pt={tableStyle}>
          {activeSolveMetricColumns.map(column => (
            <Column key={column.field} field={column.field} header={column.header} />
          ))}
        </DataTable>
      </div>
      <div className={panelClass}>
        <div className={panelTitleClass}>Solve Metrics Run Statistics</div>
        <DataTable
          value={solveMetricRunTableRows}
          emptyMessage="No solve metrics data available."
          tableStyle={{ minWidth: '96rem' }}
          pt={tableStyle}>
          {solveMetricRunTableColumns.map(column => (
            <Column
              key={column.field}
              field={column.field}
              header={column.header}
              body={solveMetricRunTableBodyByField[column.field]}
            />
          ))}
        </DataTable>
      </div>
    </div>
  )
}

export default SolveMetrics
